import { QuotationItem, QuotationParameters, Currency } from '../types';

export type LinePricingMode =
  | 'labor-internal'
  | 'labor-external'
  | 'msrp'
  | 'standard';

export interface LinePrice {
  costILS: number; // quantity × unitPriceILS
  customerPriceILS: number;
  profitILS: number; // customerPriceILS − costILS
  mode: LinePricingMode;
}

const DEFAULT_COEFFICIENT = 0.75;

/**
 * Resolve a margin coefficient, matching the legacy `value || 0.75` behavior:
 * 0, negative, undefined and null all fall back to the default, so the caller
 * can always divide by the result safely.
 */
function resolveCoefficient(value: number | undefined): number {
  return typeof value === 'number' && value > 0 ? value : DEFAULT_COEFFICIENT;
}

/** Convert a per-unit MSRP price to ILS using the quotation's exchange rates. */
function msrpUnitToILS(
  price: number,
  currency: Currency,
  params: QuotationParameters
): number {
  switch (currency) {
    case 'USD':
      return price * params.usdToIlsRate;
    case 'EUR':
      return price * params.eurToIlsRate;
    case 'NIS':
    default:
      return price;
  }
}

/**
 * The single authority for per-line pricing. Decision order (first match wins):
 * labor (internal at cost, external at its own margin) → MSRP (list price) →
 * standard (cost ÷ markup coefficient). Profit is always customerPrice − cost.
 */
export function computeLinePrice(
  item: QuotationItem,
  params: QuotationParameters
): LinePrice {
  const costILS = item.quantity * item.unitPriceILS;

  let customerPriceILS: number;
  let mode: LinePricingMode;

  if (item.itemType === 'labor') {
    if (item.isInternalLabor === false) {
      mode = 'labor-external';
      customerPriceILS =
        costILS / resolveCoefficient(params.outsourcedLaborMarkupPercent);
    } else {
      mode = 'labor-internal';
      customerPriceILS = costILS; // sold at cost, no profit
    }
  } else if (item.useMsrpPricing && item.msrpPrice && item.msrpCurrency) {
    mode = 'msrp';
    customerPriceILS =
      msrpUnitToILS(item.msrpPrice, item.msrpCurrency, params) * item.quantity;
  } else {
    mode = 'standard';
    customerPriceILS = costILS / resolveCoefficient(params.markupPercent);
  }

  return {
    costILS,
    customerPriceILS,
    profitILS: customerPriceILS - costILS,
    mode,
  };
}
